import React from "react";
import {
  FiMapPin,
  FiMessageSquare,
  FiTrash2,
  FiEdit2,
  FiExternalLink,
  FiEye,
  FiCalendar,
  FiUser,
  FiImage,
} from "react-icons/fi";
import { formatCurrency, formatDate, resolveAssetUrl, stripHtml } from "../utils/formatters";
import "./PromotionCard.css";

const PromotionCard = ({
  promotion,
  onViewDetails,
  onOpenComments,
  onDelete,
  canDelete = false,
  onEdit,
}) => {
  const {
    project_name,
    project_description,
    starting_cost,
    city_name,
    state_name,
    area_name,
    property_type,
    partner,
    user,
    created_on,
    promotion_url,
  } = promotion || {};

  const propImagesList = Array.isArray(promotion?.propertyImages) ? promotion.propertyImages : [];
  const standardImagesList = Array.isArray(promotion?.images) ? promotion.images : [];
  const allImages = propImagesList.length > 0 ? propImagesList : standardImagesList;
  const firstImage = allImages.length > 0 ? allImages[0] : null;
  const imageUrl = firstImage?.image_url || firstImage?.imagePath || firstImage?.url || "";
  const resolvedImg = imageUrl ? resolveAssetUrl(imageUrl) : null;

  const propertyTypeName =
    typeof property_type === "object" ? property_type?.name : property_type || "Property";

  const partnerName =
    partner?.first_name || partner?.name || user?.first_name || user?.name
      ? `${partner?.first_name || user?.first_name || ""} ${partner?.last_name || user?.last_name || ""}`.trim()
      : "Partner";

  const descriptionSnippet = stripHtml(project_description);

  return (
    <div className="promo-card">
      {/* Thumbnail area */}
      <div className="promo-card__media" onClick={() => onViewDetails(promotion)}>
        {resolvedImg ? (
          <img
            src={resolvedImg}
            alt={project_name || "Promotion"}
            className="promo-card__img"
            loading="lazy"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.parentElement.classList.add("promo-card__media--fallback");
            }}
          />
        ) : (
          <div className="promo-card__placeholder">
            <FiImage size={36} />
            <span>No Image</span>
          </div>
        )}

        <div className="promo-card__badges">
          {propertyTypeName && (
            <span className="promo-card__type-badge">{propertyTypeName}</span>
          )}
        </div>

        {starting_cost && (
          <div className="promo-card__price-badge">
            <span className="promo-card__price-label">Starts from</span>
            <span className="promo-card__price-val">{formatCurrency(starting_cost)}</span>
          </div>
        )}
      </div>

      {/* Content area */}
      <div className="promo-card__body">
        <h3 className="promo-card__title" onClick={() => onViewDetails(promotion)} title={project_name}>
          {project_name || "Untitled Promotion"}
        </h3>

        {/* Location info */}
        <div className="promo-card__location">
          <FiMapPin className="promo-card__loc-icon" />
          <span>
            {[area_name, city_name, state_name].filter(Boolean).join(", ") || "Location Unspecified"}
          </span>
        </div>

        {/* Description snippet */}
        {descriptionSnippet && (
          <p className="promo-card__description">
            {descriptionSnippet.length > 120
              ? `${descriptionSnippet.slice(0, 120)}...`
              : descriptionSnippet}
          </p>
        )}

        {/* Partner / date metadata */}
        <div className="promo-card__meta">
          <div className="promo-card__author" title={`Created by ${partnerName}`}>
            <FiUser className="promo-card__meta-icon" />
            <span className="promo-card__author-name">{partnerName}</span>
          </div>
          {created_on && (
            <div className="promo-card__date" title={`Created on ${formatDate(created_on)}`}>
              <FiCalendar className="promo-card__meta-icon" />
              <span>{formatDate(created_on)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Action footer */}
      <div className="promo-card__footer">
        <div className="promo-card__footer-left">
          <button
            type="button"
            className="promo-card__btn promo-card__btn--primary"
            onClick={() => onViewDetails(promotion)}
          >
            <FiEye size={14} />
            <span>Details</span>
          </button>

          <button
            type="button"
            className="promo-card__btn promo-card__btn--secondary"
            onClick={() => onOpenComments(promotion)}
            title="Comments & Discussion"
          >
            <FiMessageSquare size={14} />
            <span>Comments</span>
          </button>

          {promotion_url && (
            <a
              href={
                promotion_url.startsWith("http")
                  ? promotion_url
                  : `https://${promotion_url}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="promo-card__btn promo-card__btn--link"
              title="Visit Promotion Link"
            >
              <FiExternalLink size={14} />
            </a>
          )}
        </div>

        {(canDelete || onEdit) && (
          <div className="promo-card__footer-right">
            {onEdit && (
              <button
                type="button"
                className="promo-card__btn promo-card__btn--secondary"
                onClick={() => onEdit(promotion)}
                title="Edit Promotion"
                aria-label="Edit Promotion"
              >
                <FiEdit2 size={14} />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                className="promo-card__btn promo-card__btn--danger"
                onClick={() => onDelete(promotion)}
                title="Delete Promotion"
              >
                <FiTrash2 size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromotionCard;
